import { useCallback, useEffect, useMemo, useState } from 'react';
import schedulerService from '../services/schedulerService';
import statusWorkflowService from '../services/statusWorkflowService';
import {
  computeSetupStepStates,
  formHasStatusField,
  hasCustomFormFields,
  statusFieldsConfigured,
} from '../utils/formSetupProgressUtils';
import { countConfiguredTriggers } from '../utils/statusTriggerUtils';
import { countConfiguredAutomations } from '../utils/schedulerListUtils';

export default function useFormSetupProgress({
  formId,
  hasFormId = false,
  fields = [],
  activeSection = 'overview',
  enabled = true,
} = {}) {
  const [automationCount, setAutomationCount] = useState(0);
  const [triggerCount, setTriggerCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState('');

  const hasCustomFields = useMemo(
    () => hasCustomFormFields(fields),
    [fields]
  );
  const hasStatusField = useMemo(
    () => formHasStatusField(fields),
    [fields]
  );
  const statusConfigured = useMemo(
    () => statusFieldsConfigured(fields),
    [fields]
  );

  const loadProgressData = useCallback(async () => {
    if (!enabled || !hasFormId || !formId) {
      setAutomationCount(0);
      setLoadError('');
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setLoadError('');

    try {
      const schedulerResponse = await schedulerService.listSchedulers(formId);
      setAutomationCount(countConfiguredAutomations(schedulerResponse?.data));
    } catch (error) {
      setAutomationCount(0);
      setLoadError(error?.message || 'Failed to load setup progress.');
    } finally {
      setIsLoading(false);
    }
  }, [enabled, formId, hasFormId]);

  useEffect(() => {
    loadProgressData();
  }, [activeSection, loadProgressData]);

  const automationsConfigured = automationCount > 0 || triggerCount > 0;

  const stepStates = useMemo(
    () => computeSetupStepStates({
      hasFormId,
      hasCustomFields,
      statusConfigured,
      automationsConfigured,
      activeSection,
    }),
    [
      activeSection,
      automationsConfigured,
      hasCustomFields,
      hasFormId,
      statusConfigured,
    ]
  );

  return {
    stepStates,
    isLoading,
    loadError,
    reload: loadProgressData,
    hasCustomFields,
    hasStatusField,
    statusConfigured,
    automationsConfigured,
    automationCount,
    triggerCount,
  };
}
